import React, {useEffect, useState, useMemo, useRef} from 'react';
import {Box, Dialog, InputBase, Typography} from '@mui/material';
import {Search, KeyboardReturn, ArrowForward} from '@mui/icons-material';
import {useResourceDefinitions, useCreatePath, useTranslate} from 'react-admin';
import {useNavigate} from 'react-router-dom';
import {t} from '../../themes/statamicTokens';

/**
 * Global Cmd+K / Ctrl+K palette for quick navigation.
 * Listens globally. When open, shows a fuzzy-match list of resources + actions.
 */
const CommandPalette = () => {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState('');
    const [focusIdx, setFocusIdx] = useState(0);
    const listRef = useRef(null);

    const navigate = useNavigate();
    const resources = useResourceDefinitions();
    const createPath = useCreatePath();
    const translate = useTranslate();

    const allResources = useMemo(() => (
        Object.values(resources)
            .filter(r => r.hasList)
            .map(r => ({
                type: 'resource',
                id: `resource:${r.name}`,
                label: translate(r.options?.label ?? r.name),
                subtitle: r.name,
                to: createPath({resource: r.name, type: 'list'}),
                icon: r.icon,
            }))
    ), [resources, createPath, translate]);

    const actions = useMemo(() => ([
        {type: 'action', id: 'action:dashboard', label: translate('Go to dashboard'), subtitle: translate('Home'), to: '/'},
        ...allResources.filter(r => r.type === 'resource').map(r => ({
            type: 'action',
            id: `create:${r.subtitle}`,
            label: translate('Create %{name}', {name: r.label.toLowerCase()}),
            subtitle: translate('New record'),
            to: createPath({resource: r.subtitle, type: 'create'}),
        })),
    ]), [allResources, createPath, translate]);

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return [...allResources];
        const all = [...allResources, ...actions];
        return all.filter(item =>
            item.label.toLowerCase().includes(q)
            || item.subtitle?.toLowerCase().includes(q),
        );
    }, [query, allResources, actions]);

    useEffect(() => {
        setFocusIdx(0);
    }, [query, open]);

    useEffect(() => {
        const handler = (e) => {
            const isMod = e.metaKey || e.ctrlKey;
            if (isMod && e.key.toLowerCase() === 'k') {
                e.preventDefault();
                setOpen(v => !v);
                return;
            }
            // `/` opens the palette unless the user is typing in an input/textarea/contenteditable.
            if (e.key === '/' && !isMod && !e.altKey) {
                const target = e.target;
                const tag = target?.tagName;
                if (tag === 'INPUT' || tag === 'TEXTAREA' || target?.isContentEditable) return;
                e.preventDefault();
                setOpen(true);
            }
        };
        const openEvent = () => setOpen(true);
        window.addEventListener('keydown', handler);
        window.addEventListener('open-command-palette', openEvent);
        return () => {
            window.removeEventListener('keydown', handler);
            window.removeEventListener('open-command-palette', openEvent);
        };
    }, []);

    const close = () => {
        setOpen(false);
        setQuery('');
    };

    const select = (item) => {
        close();
        if (item?.to) navigate(item.to);
    };

    const handleKeyDown = (e) => {
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setFocusIdx(i => Math.min(filtered.length - 1, i + 1));
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setFocusIdx(i => Math.max(0, i - 1));
        } else if (e.key === 'Enter') {
            e.preventDefault();
            if (filtered[focusIdx]) select(filtered[focusIdx]);
        } else if (e.key === 'Escape') {
            close();
        }
    };

    useEffect(() => {
        if (!listRef.current) return;
        const el = listRef.current.querySelector(`[data-idx="${focusIdx}"]`);
        el?.scrollIntoView({block: 'nearest'});
    }, [focusIdx]);

    return (
        <Dialog
            open={open}
            onClose={close}
            slotProps={{
                paper: {
                    sx: {
                        borderRadius: `${t.radius.lg}px`,
                        border: `1px solid ${t.color.border.default}`,
                        boxShadow: t.shadow.modal,
                        width: '100%',
                        maxWidth: 560,
                        mt: 8,
                        alignSelf: 'flex-start',
                    },
                },
            }}
        >
            <Box sx={{display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1.5, borderBottom: `1px solid ${t.color.border.hairline}`}}>
                <Search sx={{fontSize: 18, color: t.color.text.muted}}/>
                <InputBase
                    autoFocus
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder={translate('Jump to resource or action…')}
                    sx={{flex: 1, fontSize: `${t.type.body.size}px`, color: t.color.text.primary}}
                />
                <Box
                    component="kbd"
                    sx={{
                        px: 0.75,
                        py: 0.125,
                        fontSize: 11,
                        fontFamily: t.font.mono,
                        color: t.color.text.muted,
                        border: `1px solid ${t.color.border.default}`,
                        borderRadius: `${t.radius.sm}px`,
                        backgroundColor: t.color.chip.monoBg,
                    }}
                >
                    Esc
                </Box>
            </Box>

            <Box
                ref={listRef}
                sx={{
                    maxHeight: 420,
                    overflowY: 'auto',
                }}
            >
                {filtered.length === 0 && (
                    <Box sx={{py: 4, textAlign: 'center', color: t.color.text.muted, fontSize: `${t.type.small.size}px`}}>
                        {translate('No matches')}
                    </Box>
                )}
                {filtered.map((item, i) => {
                    const active = i === focusIdx;
                    const Icon = item.icon;
                    return (
                        <Box
                            key={item.id}
                            data-idx={i}
                            onClick={() => select(item)}
                            onMouseEnter={() => setFocusIdx(i)}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1.5,
                                px: 2,
                                py: 1,
                                cursor: 'pointer',
                                backgroundColor: active ? t.color.accent.primaryTint : 'transparent',
                                borderLeft: `3px solid ${active ? t.color.accent.primary : 'transparent'}`,
                            }}
                        >
                            <Box
                                sx={{
                                    width: 26,
                                    height: 26,
                                    borderRadius: `${t.radius.sm}px`,
                                    backgroundColor: active ? t.color.surface : t.color.sidebarHover,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: t.color.text.secondary,
                                    '& .MuiSvgIcon-root': {fontSize: 14},
                                    '& svg': {width: 14, height: 14},
                                }}
                            >
                                {Icon ? <Icon/> : <ArrowForward sx={{fontSize: 14}}/>}
                            </Box>
                            <Box sx={{flex: 1, minWidth: 0}}>
                                <Typography sx={{fontSize: `${t.type.body.size}px`, color: t.color.text.primary, fontWeight: 500}}>
                                    {item.label}
                                </Typography>
                                {item.subtitle && (
                                    <Typography sx={{fontSize: `${t.type.caption.size}px`, color: t.color.text.muted}}>
                                        {item.subtitle}
                                    </Typography>
                                )}
                            </Box>
                            {active && <KeyboardReturn sx={{fontSize: 14, color: t.color.text.muted}}/>}
                        </Box>
                    );
                })}
            </Box>

            <Box sx={{
                display: 'flex', alignItems: 'center', gap: 2,
                px: 2, py: 0.75,
                borderTop: `1px solid ${t.color.border.hairline}`,
                fontSize: 11,
                color: t.color.text.muted,
                fontFamily: t.font.mono,
            }}>
                <span>↑↓ {translate('navigate')}</span>
                <span>↵ {translate('open')}</span>
                <span>esc {translate('close')}</span>
                <Box sx={{flex: 1}}/>
                <span>⌘K</span>
            </Box>
        </Dialog>
    );
};

export default CommandPalette;
